import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { socket } from '../../socket';
import { RoomInfo } from '../../types/Socket';
import { getPlayerId } from '../../utils/playerIdentity';
import './Lobby.css';

interface ChatMsg {
  sender: string;
  message: string;
  timestamp: number;
}

const PLAYER_AVATARS = [
  { emoji: '\uD83E\uDD20', bg: '#b45309' },
  { emoji: '\uD83E\uDD78', bg: '#7c3aed' },
  { emoji: '\uD83D\uDE0E', bg: '#0369a1' },
  { emoji: '\uD83E\uDDD9', bg: '#4338ca' },
  { emoji: '\uD83E\uDD16', bg: '#0f766e' },
  { emoji: '\uD83D\uDC7B', bg: '#a21caf' },
  { emoji: '\uD83E\uDD8A', bg: '#c2410c' },
  { emoji: '\uD83D\uDC09', bg: '#15803d' },
];

interface LobbyProps {
  roomCode: string;
  isHost: boolean;
  playerName: string;
  onGameStarted: () => void;
  onLeave: () => void;
}

const Lobby: React.FC<LobbyProps> = ({
  roomCode,
  isHost,
  playerName,
  onGameStarted,
  onLeave,
}) => {
  const [roomInfo, setRoomInfo] = useState<RoomInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(socket.connected);
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  const joinUrl = `${window.location.origin}${window.location.pathname}?room=${roomCode}`;

  const onGameStartedRef = React.useRef(onGameStarted);
  onGameStartedRef.current = onGameStarted;

  useEffect(() => {
    const onRoomUpdate = (data: RoomInfo) => setRoomInfo(data);
    const onGameUpdate = () => onGameStartedRef.current();
    const onError = ({ message }: { message: string }) => setError(message);
    const onChat = (data: ChatMsg) => setChatMessages((prev) => [...prev.slice(-50), data]);

    // Re-join the room on socket reconnect (new socket ID needs fresh mapping)
    let hasConnectedOnce = false;
    const onConnect = () => {
      setConnected(true);
      if (!hasConnectedOnce) {
        hasConnectedOnce = true;
        socket.emit('request_room_info');
        return;
      }
      // Reconnection — re-register with the server under the same name
      socket.emit('join_room', {
        roomCode,
        playerName,
        playerId: getPlayerId(),
      });
    };
    const onDisconnect = () => setConnected(false);

    socket.on('room_update', onRoomUpdate);
    socket.on('game_update', onGameUpdate);
    socket.on('error_msg', onError);
    socket.on('chat_message', onChat);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // If already connected, request room info immediately
    if (socket.connected) {
      hasConnectedOnce = true;
      socket.emit('request_room_info');
    }

    return () => {
      socket.off('room_update', onRoomUpdate);
      socket.off('game_update', onGameUpdate);
      socket.off('error_msg', onError);
      socket.off('chat_message', onChat);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chatMessages.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleSendChat = () => {
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    socket.emit('chat_message', { message: trimmed });
    setChatInput('');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      await navigator.clipboard.writeText(roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareText = `Join my ACE card game! 🃏\nRoom Code: ${roomCode}\nJoin here: ${joinUrl}`;

  const handleInvite = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'ACE Card Game', text: shareText });
      } catch { /* user cancelled share */ }
    } else {
      window.open(
        `https://wa.me/?text=${encodeURIComponent(shareText)}`,
        '_blank',
        'noopener'
      );
    }
  };

  const [showStartChoice, setShowStartChoice] = useState(false);

  const handleStart = () => {
    setError('');
    if (emptyHumanSlots > 0) {
      setShowStartChoice(true);
    } else {
      socket.emit('start_game');
    }
  };

  const handleStartWithBots = () => {
    setShowStartChoice(false);
    setError('');
    socket.emit('start_game', { fillWithBots: true });
  };

  const handleStartWithout = () => {
    setShowStartChoice(false);
    setError('');
    const currentTotal = (roomInfo?.humanSlotsFilled ?? 0) + (roomInfo?.numComputers ?? 0);
    if (currentTotal < 3) {
      setError('Need at least 3 players to start');
      return;
    }
    socket.emit('start_game', { fillWithBots: false });
  };

  const handleLeave = () => {
    onLeave();
  };

  const players = roomInfo?.players || [];
  const effectiveIsHost = roomInfo ? roomInfo.isHost : isHost;
  const canStart = effectiveIsHost && roomInfo && roomInfo.maxPlayers >= 3;
  const emptyHumanSlots = roomInfo
    ? roomInfo.humanSlotsTotal - roomInfo.humanSlotsFilled
    : 0;
  const canInvite = roomInfo
    ? roomInfo.humanSlotsFilled < roomInfo.maxPlayers
    : true;
  const connectedHumans = players.filter((p) => !p.isComputer && p.isConnected).length;
  const totalBots = players.filter((p) => p.isComputer).length;
  const openSeats = roomInfo ? Math.max(0, roomInfo.maxPlayers - players.length) : 0;

  return (
    <div className="lobby-screen">
      <motion.div
        className="lobby-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="lobby-title">Game Lobby</h2>
        <div className={`lobby-connection-pill ${connected ? 'online' : 'offline'}`}>
          <span className="lobby-connection-dot" />
          {connected ? 'Everyone can join now' : 'Reconnecting to room...'}
        </div>

        {/* Room code display */}
        <div className="room-code-display">
          <span className="room-code-label">Room Code</span>
          <span className="room-code-value">{roomCode}</span>
          <div className="room-code-actions">
            <button className="copy-btn" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            {canInvite && (
              <button className="invite-btn" onClick={handleInvite}>
                Invite
              </button>
            )}
          </div>
        </div>

        {/* QR Code */}
        <div className="qr-wrapper">
          <QRCodeSVG
            value={joinUrl}
            size={140}
            bgColor="transparent"
            fgColor="#facc15"
            level="M"
          />
          <span className="qr-hint">Scan to join</span>
        </div>

        {/* Room settings */}
        {roomInfo && (
          <div className="lobby-settings">
            <span className="lobby-settings-label">Turn Timer</span>
            <span className="lobby-settings-value">
              {roomInfo.settings.turnDuration > 0 ? `${roomInfo.settings.turnDuration}s` : 'Off'}
            </span>
          </div>
        )}

        <div className="lobby-summary-row">
          <div className="lobby-summary-chip">
            <strong>{connectedHumans}</strong>
            <span>Players here</span>
          </div>
          <div className="lobby-summary-chip">
            <strong>{totalBots}</strong>
            <span>Bots</span>
          </div>
          <div className="lobby-summary-chip">
            <strong>{openSeats}</strong>
            <span>Open seats</span>
          </div>
        </div>

        {/* Player list */}
        <div className="lobby-players">
          <span className="lobby-players-title">
            Players ({players.length}/{roomInfo?.maxPlayers || '?'})
          </span>
          <div className="lobby-player-list">
            {players.map((p) => (
              <div
                key={p.index}
                className={`lobby-player ${!p.isConnected && !p.isComputer ? 'disconnected' : ''}`}
              >
                <span
                  className="lobby-avatar"
                  style={{ background: PLAYER_AVATARS[p.index % PLAYER_AVATARS.length].bg }}
                >
                  {p.isComputer ? '🤖' : PLAYER_AVATARS[p.index % PLAYER_AVATARS.length].emoji}
                </span>
                <span className="lobby-player-name">{p.name}</span>
                {p.isHost && <span className="lobby-tag host">Host</span>}
                {p.isComputer && <span className="lobby-tag bot">Bot</span>}
              </div>
            ))}

            {/* Empty slots */}
            {roomInfo &&
              Array.from(
                { length: roomInfo.humanSlotsTotal - roomInfo.humanSlotsFilled },
                (_, i) => (
                  <div key={`empty-${i}`} className="lobby-player empty">
                    <span className="lobby-avatar empty-avatar">?</span>
                    <span className="lobby-player-name">Waiting...</span>
                  </div>
                )
              )}
          </div>
        </div>

        {/* Chat */}
        <div className="lobby-chat">
          <span className="lobby-chat-title">Chat</span>
          <div className="lobby-chat-messages">
            {chatMessages.length === 0 && (
              <span className="lobby-chat-empty">No messages yet…</span>
            )}
            {chatMessages.map((msg, i) => (
              <div
                key={`${msg.timestamp}-${i}`}
                className={`chat-msg ${msg.sender === playerName ? 'mine' : ''}`}
              >
                <span className="chat-sender">{msg.sender}</span>
                <span className="chat-text">{msg.message}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form
            className="lobby-chat-input"
            onSubmit={(e) => { e.preventDefault(); handleSendChat(); }}
          >
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Type a message…"
              maxLength={200}
            />
            <button type="submit" disabled={!chatInput.trim()}>Send</button>
          </form>
        </div>

        {/* Actions */}
        <div className="lobby-actions">
          {canStart && !showStartChoice && (
            <motion.button
              className="lobby-start-btn"
              onClick={handleStart}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Start Game
            </motion.button>
          )}
          {canStart && showStartChoice && (
            <div className="start-choice">
              <p className="start-choice-text">
                {emptyHumanSlots} {emptyHumanSlots === 1 ? 'slot is' : 'slots are'} empty. How would you like to start?
              </p>
              <div className="start-choice-btns">
                <motion.button
                  className="lobby-start-btn start-choice-btn"
                  onClick={handleStartWithBots}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Fill with Bots
                </motion.button>
                <motion.button
                  className="lobby-start-btn start-choice-btn secondary"
                  onClick={handleStartWithout}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Just us
                </motion.button>
              </div>
              <button className="start-choice-cancel" onClick={() => setShowStartChoice(false)}>
                Cancel
              </button>
            </div>
          )}
          {!effectiveIsHost && (
            <p className="lobby-waiting">Waiting for host to start the game...</p>
          )}
          {effectiveIsHost && !showStartChoice && (
            <p className="lobby-host-tip">
              {emptyHumanSlots > 0
                ? `You can start now or wait for ${emptyHumanSlots} more ${emptyHumanSlots === 1 ? 'player' : 'players'}.`
                : 'Everyone is in. Start whenever you are ready.'}
            </p>
          )}
        </div>

        {error && <p className="lobby-error">{error}</p>}
      </motion.div>

      <button className="lobby-back-btn" onClick={handleLeave}>
        &larr; Back
      </button>
    </div>
  );
};

export default Lobby;
